import type { Dispatch, SetStateAction } from 'react'
import type { Panel } from '@/types/panels'
import type { PanelSuggestions, SearchState } from '@/types/search'
import {
  navigateToBendingMomentSearch,
  navigateToPanel,
  navigateToProductSearch,
  navigateToSectionModulusSearch,
} from '@/utils/navigationUtils'

type SearchStateSetters = {
  setProductName: (value: string) => void
  setShowSuggestions: (value: boolean) => void
  // The real setter comes from useState in useSearchState, so callers may pass
  // an updater function (used by the ArrowUp/ArrowDown handlers below).
  setSelectedIndex: Dispatch<SetStateAction<number>>
  setSearchError: (value: string) => void
}

export const useSearchHandlers = (
  searchState: SearchState,
  setters: SearchStateSetters,
  suggestions: Panel[],
  modulusSuggestions: PanelSuggestions,
  bendingSuggestions: PanelSuggestions,
) => {
  const handleProductSearch = () => {
    navigateToProductSearch(searchState.productName)
  }

  const handleSectionModulusSearch = () => {
    const allPanels = [
      ...modulusSuggestions.exact,
      ...modulusSuggestions.above,
      ...modulusSuggestions.below,
    ]
    if (allPanels.length > 0) {
      const bestPanel = modulusSuggestions.exact[0] || allPanels[0]
      navigateToPanel(bestPanel)
    } else if (searchState.sectionModulus.trim()) {
      navigateToSectionModulusSearch(searchState.sectionModulus, searchState.sectionModulusUnit)
    }
  }

  const handleBendingMomentSearch = () => {
    const allPanels = [
      ...bendingSuggestions.exact,
      ...bendingSuggestions.above,
      ...bendingSuggestions.below,
    ]
    if (allPanels.length > 0) {
      const bestPanel = bendingSuggestions.exact[0] || allPanels[0]
      navigateToPanel(bestPanel)
    } else if (searchState.bendingMoment.trim()) {
      navigateToBendingMomentSearch(searchState.bendingMomentUnit)
    }
  }

  const selectSuggestion = (panel: Panel) => {
    setters.setProductName(panel.name)
    setters.setShowSuggestions(false)
    setters.setSelectedIndex(-1)
    navigateToPanel(panel)
  }

  const selectModulusPanel = (panel: Panel) => {
    navigateToPanel(panel)
  }

  const selectBendingPanel = (panel: Panel) => {
    navigateToPanel(panel)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (searchState.selectedIndex >= 0 && suggestions[searchState.selectedIndex]) {
        selectSuggestion(suggestions[searchState.selectedIndex])
      } else if (suggestions.length > 0) {
        selectSuggestion(suggestions[0])
      } else if (searchState.productName.trim()) {
        setters.setSearchError('main_page.search.by_name.no_results')
        setters.setShowSuggestions(false)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setters.setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setters.setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
    } else if (e.key === 'Escape') {
      setters.setShowSuggestions(false)
      setters.setSelectedIndex(-1)
    }
  }

  const handleInputChange = (value: string) => {
    setters.setProductName(value)
    setters.setShowSuggestions(value.length > 0)
    setters.setSelectedIndex(-1)
    setters.setSearchError('')
  }

  return {
    handleProductSearch,
    handleSectionModulusSearch,
    handleBendingMomentSearch,
    selectSuggestion,
    selectModulusPanel,
    selectBendingPanel,
    handleKeyDown,
    handleInputChange,
  }
}
